import { ActionContext } from 'vuex';
import { Equal, IQueryCommandOptions, IWhereCommand } from '@llws/dynamic-router-utils';
import {
  IFilterPayload,
  snakeToCamel,
} from '@/shared';
import { snakeToCapitalized } from '@/shared/helpers/snake-to-capitalized';
import { IImporterField, IImporterFieldsStoreState } from '@/shared/interfaces/importer-field';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/client-api/data-source-columns';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  relations: [
    {
      path: 'source',
      alias: 'src',
    },
  ],
  where: [
    Equal('ignore_on_update', '0'),
  ],
  limit: 9999,
  orderBy: [{ field: 'created', direction: 'desc' }],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IImporterField>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'importer-fields/'
  state: {
    ...base.state,
    fieldSource: {},
    changedDateSourcesNames: '',
  } as IImporterFieldsStoreState,
  getters: {
    ...base.getters,
    fieldSource: (state: IImporterFieldsStoreState): {[key: string]: string} => state.fieldSource || {},
    dataSources: (state: IImporterFieldsStoreState): string[] => {
      const sources: string[] = Object.keys(state.fieldSource).reduce((result: string[], field) => {
        const fieldSources = state.fieldSource[field].split(', ').filter((source) => !result.includes(source));
        return [...result, ...fieldSources];
      }, []);
      return sources;
    },
  },
  mutations: {
    ...base.mutations,
    setFieldSource: (state: IImporterFieldsStoreState) => {
      const fieldSource: {[k: string]: string} = {};
      const data = state.list?.data;
      if (data) {
        data.forEach((field: IImporterField) => {
          const originKey = `${snakeToCamel(field.tableName)}_${snakeToCamel(field.columnName)}`;
          if (fieldSource[originKey] && field.source?.sourceName) {
            fieldSource[originKey] = `${fieldSource[originKey]}, ${snakeToCapitalized(field.source.sourceName)}`;
          } else {
            fieldSource[originKey] = snakeToCapitalized(field.source?.sourceName || '');
          }
        });
      }
      state.fieldSource = fieldSource;
    },
  },
  actions: {
    ...base.actions,
    getImporters: async (ctx: ActionContext<any, any>, payload?: IFilterPayload) => {
      await base.actions.get(ctx, {
        filters: {
          where: [
            {
              field: 'src.active',
              operator: '',
              notOperator: false,
              value: '1',
            },
            {
              field: 'src.scheduled',
              operator: '',
              notOperator: false,
              value: '1',
            },
          ] as IWhereCommand[],
        } as IQueryCommandOptions,
      });
      ctx.commit('setFieldSource');
    },
  },
};
